import { useLivePoll } from '@polf/shared/hooks'
import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import {
  useCurrentPlayerEventMatchesQuery,
  useCurrentPlayerLeaderBoardQuery,
} from '../__generated__/hooks'
import LeaderboardView from '../components/Leaderboard'
import MatchView from '../components/Leaderboard/MatchView'
import Toggle from '../components/Leaderboard/Toggle'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import type { CurrentPlayerCtx } from '../PlayerShell'

/**
 * Authenticated player's leaderboard tab (Topp / Leaderboard). Wraps the shared
 * LeaderboardView with player-key auth and self-highlight.
 */
const Leaderboard = () => {
  const { player } = useOutletContext<{ player: CurrentPlayerCtx }>()
  const online = useOnlineStatus()
  const { data, loading, error, refetch } = useCurrentPlayerLeaderBoardQuery({
    fetchPolicy: 'cache-and-network',
  })
  // Standings only move while scores are being entered; after the round they
  // are final unless a TD reopens to correct one.
  useLivePoll(refetch, player.polfEvent.isReadOnly ? 60_000 : 10_000, true, {
    idleAfterMs: 5 * 60_000,
    idleIntervalMs: 60_000,
  })

  // Match is the default when the event has one: it is the competition the
  // round is being played for. The stroke leaderboard is still there, because
  // a match round produces an ordinary card too.
  const matchPlay = Boolean(player.polfEvent.matchPlay)
  const { data: matchData, refetch: refetchMatches } =
    useCurrentPlayerEventMatchesQuery({
      fetchPolicy: 'cache-and-network',
      skip: !matchPlay,
    })
  useLivePoll(
    refetchMatches,
    player.polfEvent.isReadOnly ? 60_000 : 10_000,
    matchPlay,
    { idleAfterMs: 5 * 60_000, idleIntervalMs: 60_000 }
  )
  const matches = matchData?.currentPlayerEventMatches ?? []
  const [view, setView] = useState<'match' | 'strokes'>('match')

  if (loading && !data) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster leaderboard…
      </section>
    )
  }
  // Error page only when there's nothing cached to show — a failed poll must not
  // blank a rendered leaderboard, and the fall-through below would claim the
  // event has no standings at all.
  if (error && !data) {
    return online ? (
      <section className="px-4 py-8 text-center text-flag">
        Kunne ikke laste leaderboardet. Sjekk nettet og prøv igjen.
      </section>
    ) : (
      <section className="px-4 py-8 text-center text-ink-500">
        Ingen nett. Leaderboardet lastes så snart du er tilkoblet igjen.
      </section>
    )
  }

  const strokes = (
    <LeaderboardView
      leaderboard={data?.currentPlayerLeaderBoard ?? null}
      youId={player.id}
      yourTeamId={player.team?.id ?? null}
      ranking={player.polfEvent.ranking}
      courseHoles={player.polfEvent.course?.holes ?? []}
    />
  )

  if (!matchPlay || matches.length === 0) return strokes

  return (
    <div className="flex flex-col gap-2">
      <div className="px-4 pt-3">
        <Toggle
          options={[
            { value: 'match', label: 'Match' },
            { value: 'strokes', label: 'Individuelt' },
          ]}
          value={view}
          onChange={setView}
        />
      </div>
      {view === 'match' ? (
        <MatchView matches={matches} youId={player.id} />
      ) : (
        strokes
      )}
    </div>
  )
}

export default Leaderboard
